import { A, useNavigate, useParams, useSearchParams } from "@solidjs/router";
import {
  createEffect,
  createMemo,
  createResource,
  createSignal,
  For,
  onCleanup,
  onMount,
  Show,
} from "solid-js";

import {
  fetchFirm,
  fetchFirms,
  fetchPeers,
  type AiPosture,
  type Financials,
  type Firm as FirmT,
  type FirmSummary,
  type Operations,
  type PeerMedian,
  type PeerRow,
} from "../api";
import { AXIS_DEFS, type AxisDef } from "../lib/framework";

type TabId = "overview" | "operations" | "financials" | "ai_posture" | "framework";
const TABS: TabId[] = ["overview", "operations", "financials", "ai_posture", "framework"];
const TAB_LABEL: Record<TabId, string> = {
  overview: "Overview",
  operations: "Operations",
  financials: "Financials",
  ai_posture: "AI posture",
  framework: "Framework",
};
// One-line orientation under each tab: maps the tabs to the three lenses + deep read.
const TAB_HINT: Record<TabId, string> = {
  overview: "identity and profile",
  operations: "lens 1: how it runs",
  financials: "lens 2: the numbers",
  ai_posture: "lens 3: does AI break it",
  framework: "the deep five-axis read",
};

function FirmLogo(props: { slug: string; ticker: string | null; hasLogo: boolean; hasIcon: boolean; size?: "sm" | "md" }) {
  const size = props.size ?? "md";
  const [errored, setErrored] = createSignal(false);
  const kind = () => (props.hasLogo ? "logo" : props.hasIcon ? "icon" : null);
  const src = () => {
    const k = kind();
    return k ? `/api/firms/${encodeURIComponent(props.slug)}/branding/${k}` : null;
  };
  return (
    <Show
      when={src() && !errored()}
      fallback={<div class={`logo-chip ${size === "sm" ? "logo-chip-sm" : ""}`}>{props.ticker ?? "?"}</div>}
    >
      <img
        class={`firm-logo ${size === "sm" ? "firm-logo-sm" : ""}`}
        src={src()!}
        alt={`${props.ticker ?? props.slug} logo`}
        onError={() => setErrored(true)}
      />
    </Show>
  );
}

function fmtEmployees(n: number | null | undefined): string {
  if (n == null) return "n/a";
  if (n >= 1000) return `~${Math.round(n / 1000).toLocaleString()}K`;
  return n.toLocaleString();
}

function fmtMoney(v: number | null | undefined): string {
  if (v == null) return "n/a";
  const abs = Math.abs(v);
  const sign = v < 0 ? "-" : "";
  if (abs >= 1e12) return `${sign}$${(abs / 1e12).toFixed(2)}T`;
  if (abs >= 1e9) return `${sign}$${(abs / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(0)}M`;
  return `${sign}$${abs.toLocaleString()}`;
}

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

function Counter(props: {
  value: number | null;
  prefix?: string;
  suffix?: string;
  decimals?: number;
  format?: "comma" | "default";
  duration?: number;
}) {
  const [current, setCurrent] = createSignal(0);
  let raf = 0;

  createEffect(() => {
    if (props.value == null) return;
    const target = props.value;
    const duration = props.duration ?? 1200;
    const start = performance.now();
    cancelAnimationFrame(raf);
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      setCurrent(target * easeOutCubic(t));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
  });

  onCleanup(() => cancelAnimationFrame(raf));

  const text = () => {
    if (props.value == null) return "n/a";
    const v = current();
    const decimals = props.decimals ?? 0;
    if (props.format === "comma") return Math.round(v).toLocaleString();
    return v.toFixed(decimals);
  };

  return (
    <>
      {props.prefix ?? ""}
      {text()}
      {props.suffix ?? ""}
    </>
  );
}

interface Kpi {
  label: string;
  value: number | null;
  prefix?: string;
  suffix?: string;
  decimals?: number;
  format?: "comma" | "default";
  sub?: string;
  definition: string;
  formula?: string;
  source: string;
}

function buildKpis(firm: FirmT): Kpi[] {
  const inc = firm.financials?.annual?.income;
  const cf = firm.financials?.annual?.cash_flow;
  const fy = firm.financials?.fiscal_year;
  const revB = inc?.revenue != null ? inc.revenue / 1e9 : null;
  const opB = inc?.operating_income != null ? inc.operating_income / 1e9 : null;
  const netB = inc?.net_income != null ? inc.net_income / 1e9 : null;
  const ebitdaB = inc?.ebitda != null ? inc.ebitda / 1e9 : null;
  const fcfB = cf?.free_cf != null ? cf.free_cf / 1e9 : null;
  const opMargin =
    inc?.operating_income != null && inc?.revenue != null && inc.revenue !== 0
      ? (inc.operating_income / inc.revenue) * 100
      : null;
  const rdB = inc?.rd != null ? inc.rd / 1e9 : null;

  const API_SRC = "10-K annual filing, pulled via Massive.com";

  const mcapB = firm.market_cap != null ? firm.market_cap / 1e9 : null;

  return [
    {
      label: "Market cap",
      value: mcapB,
      prefix: "$",
      suffix: "B",
      decimals: 1,
      definition:
        "Market capitalization at last pull: most recent close price multiplied by weighted shares outstanding. Reflects the market's snapshot valuation, not a fundamental.",
      formula: "close_price x weighted_shares_outstanding",
      source: "/v3/reference/tickers, pulled via Massive.com",
    },
    {
      label: `Revenue (FY${fy ?? ""})`,
      value: revB,
      prefix: "$",
      suffix: "B",
      decimals: 1,
      definition: "Total reported revenue (net sales) for the fiscal year. The top line.",
      source: API_SRC,
    },
    {
      label: "Operating income",
      value: opB,
      prefix: "$",
      suffix: "B",
      decimals: 1,
      sub: opMargin != null ? `${opMargin.toFixed(1)}% margin` : undefined,
      definition: "Profit from core operations: gross profit minus operating expenses (SG&A, R&D, D&A). Excludes interest and taxes.",
      formula: "operating margin = operating_income / revenue",
      source: API_SRC,
    },
    {
      label: "EBITDA",
      value: ebitdaB,
      prefix: "$",
      suffix: "B",
      decimals: 1,
      definition: "Earnings before interest, taxes, depreciation, and amortization. Used as an operating cash proxy that strips out financing and accounting policy effects.",
      formula: "operating_income + depreciation + amortization",
      source: "computed by the financials API from the 10-K",
    },
    {
      label: "Net income",
      value: netB,
      prefix: "$",
      suffix: "B",
      decimals: 1,
      definition: "Bottom-line profit attributable to the consolidated entity after taxes, interest, and minority interest.",
      source: API_SRC,
    },
    {
      label: "Free cash flow",
      value: fcfB,
      prefix: "$",
      suffix: "B",
      decimals: 1,
      definition: "Cash left after operating expenses and capital investment. The cash available for dividends, buybacks, debt paydown, or M&A.",
      formula: "operating_cash_flow - |capex|",
      source: "computed from 10-K cash flow statement",
    },
    {
      label: "R&D spend",
      value: rdB,
      prefix: "$",
      suffix: "B",
      decimals: 1,
      sub: rdB != null && revB ? `${((rdB / revB) * 100).toFixed(1)}% of rev` : undefined,
      definition: "Research & development expense for the fiscal year. Some firms (Walmart, McDonald's, banks) don't break R&D out as a separate line; shows n/a when undisclosed.",
      formula: "% of rev = rd / revenue",
      source: API_SRC,
    },
    {
      label: "Employees",
      value: firm.employees,
      format: "comma",
      definition: "Approximate total employees (full-time + part-time, sometimes including franchised or managed staff for hospitality + restaurants). Hand-entered from each firm's 10-K Item 1 disclosure; drifts year to year as new filings come out.",
      source: "10-K Item 1, Business (hand-entered)",
    },
  ];
}

function KpiCard(props: { k: Kpi }) {
  const k = () => props.k;
  return (
    <div class="kpi">
      <div class="kpi-head">
        <div class="kpi-label">{k().label}</div>
        <span class="kpi-info-wrap">
          <button class="kpi-info" type="button" aria-label={`What is ${k().label}?`}>
            ?
          </button>
          <div class="kpi-tooltip" role="tooltip">
            <div class="kpi-def">{k().definition}</div>
            <Show when={k().formula}>
              <div class="kpi-formula">
                <span class="lbl">Formula</span> <code>{k().formula}</code>
              </div>
            </Show>
            <div class="kpi-src">
              <span class="lbl">Source</span> {k().source}
            </div>
          </div>
        </span>
      </div>
      <div class="kpi-value">
        <Counter
          value={k().value}
          prefix={k().prefix}
          suffix={k().suffix}
          decimals={k().decimals}
          format={k().format}
        />
      </div>
      <Show when={k().sub}>
        <div class="kpi-sub">{k().sub}</div>
      </Show>
    </div>
  );
}

function addressLine(firm: FirmT): string | null {
  const parts = [firm.address1, firm.city, firm.state, firm.postal_code].filter(
    (x): x is string => !!x && x.length > 0,
  );
  return parts.length ? parts.join(", ") : null;
}

function OverviewTab(props: { firm: FirmT }) {
  const firm = () => props.firm;
  const addr = () => addressLine(firm());
  const hasProfile = () =>
    !!(firm().description || firm().homepage_url || firm().legal_name);

  return (
    <div class="tab-pane">
      <Show when={hasProfile()}>
        <div class="card profile-card">
          <h3>Company</h3>
          <Show when={firm().legal_name}>
            <div class="profile-legal">{firm().legal_name}</div>
          </Show>
          <Show when={firm().description}>
            <p class="profile-desc">{firm().description}</p>
          </Show>
          <Show when={firm().homepage_url}>
            <a
              class="profile-link"
              href={firm().homepage_url!}
              target="_blank"
              rel="noopener noreferrer"
            >
              {firm().homepage_url!.replace(/^https?:\/\//, "").replace(/\/$/, "")}
            </a>
          </Show>
        </div>
      </Show>
      <div class="overview-grid">
        <div class="card">
          <h3>Identifiers</h3>
          <dl class="kv">
            <div class="row"><dt>F500 rank</dt><dd>#{firm().f500_rank}</dd></div>
            <div class="row"><dt>Ticker</dt><dd>{firm().ticker ?? "n/a"}</dd></div>
            <Show when={firm().primary_exchange}>
              <div class="row"><dt>Exchange</dt><dd>{firm().primary_exchange}</dd></div>
            </Show>
            <div class="row"><dt>Sector</dt><dd>{firm().sector}</dd></div>
            <Show when={firm().industry}>
              <div class="row"><dt>Industry</dt><dd>{firm().industry}</dd></div>
            </Show>
            <Show when={firm().sic_code}>
              <div class="row"><dt>SIC</dt><dd>{firm().sic_code} {firm().sic_description ? `(${firm().sic_description})` : ""}</dd></div>
            </Show>
            <div class="row"><dt>HQ</dt><dd>{firm().hq}</dd></div>
            <Show when={addr()}>
              <div class="row"><dt>Address</dt><dd>{addr()}</dd></div>
            </Show>
            <div class="row"><dt>Employees</dt><dd>{fmtEmployees(firm().employees)}</dd></div>
            <Show when={firm().market_cap}>
              <div class="row"><dt>Market cap</dt><dd>{fmtMoney(firm().market_cap)}</dd></div>
            </Show>
            <Show when={firm().list_date}>
              <div class="row"><dt>Listed</dt><dd>{firm().list_date}</dd></div>
            </Show>
            <Show when={firm().cik}>
              <div class="row"><dt>CIK</dt><dd><code>{firm().cik}</code></dd></div>
            </Show>
            <Show when={firm().composite_figi}>
              <div class="row"><dt>FIGI</dt><dd><code>{firm().composite_figi}</code></dd></div>
            </Show>
          </dl>
        </div>
      </div>
    </div>
  );
}

function OperationsTab(props: { ops: Operations }) {
  const ops = () => props.ops;
  return (
    <div class="tab-pane">
      <Show when={ops().scale && ops().scale.length}>
        <div class="ops-scale">
          <For each={ops().scale}>
            {(s) => (
              <div class="ops-stat">
                <div class="k">{s.label}</div>
                <div class="v">{s.value}</div>
              </div>
            )}
          </For>
        </div>
      </Show>
      <div class="ops-cols-3">
        <Show when={ops().rhythm && ops().rhythm.length}>
          <div class="card">
            <h3>Rhythm</h3>
            <ul class="rhythm">
              <For each={ops().rhythm}>
                {(r) => (
                  <li>
                    <span class="cadence">{r.cadence}</span>
                    <span class="what">{r.what}</span>
                  </li>
                )}
              </For>
            </ul>
          </div>
        </Show>
        <Show when={ops().core_processes && ops().core_processes.length}>
          <div class="card">
            <h3>Core processes</h3>
            <ol class="processes">
              <For each={ops().core_processes}>{(p) => <li>{p}</li>}</For>
            </ol>
          </div>
        </Show>
        <Show when={ops().failure_modes && ops().failure_modes.length}>
          <div class="card">
            <h3>Failure modes</h3>
            <ul class="bulleted warn">
              <For each={ops().failure_modes}>{(p) => <li>{p}</li>}</For>
            </ul>
          </div>
        </Show>
      </div>
    </div>
  );
}

function FinancialsTab(props: { fin: Financials }) {
  const fin = () => props.fin;
  const income = () => props.fin.annual.income;
  const cashFlow = () => props.fin.annual.cash_flow;
  const balance = () => props.fin.annual.balance;
  return (
    <div class="tab-pane">
      <div class="fin-source-bar">
        <span>Source: Massive.com</span>
        <span>FY{fin().fiscal_year}</span>
        <span>as of {fin().as_of}</span>
        <span>filed {fin().filing_date}</span>
        <span>pulled {fin().source.pulled_at.slice(0, 10)}</span>
      </div>
      <div class="fin-tables">
        <div class="card">
          <h3>Income (annual)</h3>
          <dl class="kv">
            <div class="row"><dt>Revenue</dt><dd>{fmtMoney(income().revenue)}</dd></div>
            <div class="row"><dt>Cost of revenue</dt><dd>{fmtMoney(income().cost_of_revenue)}</dd></div>
            <div class="row"><dt>Gross profit</dt><dd>{fmtMoney(income().gross_profit)}</dd></div>
            <div class="row"><dt>SG&A</dt><dd>{fmtMoney(income().sga)}</dd></div>
            <div class="row"><dt>R&D</dt><dd>{fmtMoney(income().rd)}</dd></div>
            <div class="row"><dt>Operating income</dt><dd>{fmtMoney(income().operating_income)}</dd></div>
            <div class="row"><dt>EBITDA</dt><dd>{fmtMoney(income().ebitda)}</dd></div>
            <div class="row"><dt>Net income</dt><dd>{fmtMoney(income().net_income)}</dd></div>
            <div class="row"><dt>Diluted EPS</dt><dd>{income().diluted_eps ?? "n/a"}</dd></div>
          </dl>
        </div>
        <div class="card">
          <h3>Cash flow (annual)</h3>
          <dl class="kv">
            <div class="row"><dt>Operating CF</dt><dd>{fmtMoney(cashFlow().operating_cf)}</dd></div>
            <div class="row"><dt>Capex</dt><dd>{fmtMoney(cashFlow().capex)}</dd></div>
            <div class="row"><dt>Free CF</dt><dd>{fmtMoney(cashFlow().free_cf)}</dd></div>
            <div class="row"><dt>Dividends</dt><dd>{fmtMoney(cashFlow().dividends)}</dd></div>
          </dl>
        </div>
        <div class="card">
          <h3>Balance sheet</h3>
          <dl class="kv">
            <div class="row"><dt>Total assets</dt><dd>{fmtMoney(balance().total_assets)}</dd></div>
            <div class="row"><dt>Total liabilities</dt><dd>{fmtMoney(balance().total_liabilities)}</dd></div>
            <div class="row"><dt>Total equity</dt><dd>{fmtMoney(balance().total_equity)}</dd></div>
            <div class="row"><dt>Cash</dt><dd>{fmtMoney(balance().cash)}</dd></div>
            <div class="row"><dt>Inventories</dt><dd>{fmtMoney(balance().inventories)}</dd></div>
            <div class="row"><dt>PP&E (net)</dt><dd>{fmtMoney(balance().ppe_net)}</dd></div>
            <div class="row"><dt>Long-term debt</dt><dd>{fmtMoney(balance().long_term_debt)}</dd></div>
            <div class="row"><dt>Goodwill</dt><dd>{fmtMoney(balance().goodwill)}</dd></div>
          </dl>
        </div>
        <Show when={fin().ttm}>
          {(ttm) => (
            <div class="card">
              <h3>Trailing twelve months</h3>
              <dl class="kv">
                <div class="row"><dt>Period end</dt><dd>{ttm().period_end}</dd></div>
                <div class="row"><dt>Revenue</dt><dd>{fmtMoney(ttm().revenue)}</dd></div>
                <div class="row"><dt>Operating income</dt><dd>{fmtMoney(ttm().operating_income)}</dd></div>
                <div class="row"><dt>Net income</dt><dd>{fmtMoney(ttm().net_income)}</dd></div>
                <div class="row"><dt>EBITDA</dt><dd>{fmtMoney(ttm().ebitda)}</dd></div>
              </dl>
            </div>
          )}
        </Show>
      </div>
    </div>
  );
}

function AiPostureTab(props: { posture: AiPosture }) {
  const posture = () => props.posture;
  return (
    <div class="tab-pane">
      <div class="ai-grid">
        <div class="ai-cell survives">
          <h3>What survives</h3>
          <p>{posture().what_survives}</p>
        </div>
        <div class="ai-cell compresses">
          <h3>What compresses</h3>
          <p>{posture().what_compresses}</p>
        </div>
        <div class="ai-cell timeline">
          <h3>Timeline</h3>
          <p>{posture().timeline}</p>
        </div>
        <div class="ai-cell signal">
          <h3>Signal to watch</h3>
          <p>{posture().signal}</p>
        </div>
      </div>
    </div>
  );
}

function AxisInfo(props: { def: AxisDef }) {
  return (
    <span class="kpi-info-wrap">
      <button class="kpi-info" type="button" aria-label={`What is ${props.def.label}?`}>?</button>
      <div class="kpi-tooltip" role="tooltip">
        <div class="kpi-def">{props.def.definition}</div>
        <Show when={props.def.example}>
          <div class="kpi-formula">
            <span class="lbl">Example</span> {props.def.example}
          </div>
        </Show>
        <div class="kpi-src">
          <span class="lbl">Source</span> {props.def.source}
        </div>
      </div>
    </span>
  );
}

/**
 * Renders a framework field. Restyled records store Moat / AI-risk as
 * newline-separated bullets; those render as a list. Single-line values
 * (the clean prose firms, and clearing_layer / verification / fork) render
 * as plain text.
 */
function FwValue(props: { value: string | null | undefined }) {
  const lines = () =>
    (props.value ?? "")
      .split("\n")
      .map((l) => l.replace(/^[-*•]\s*/, "").trim())
      .filter(Boolean);
  return (
    <Show when={(props.value ?? "").includes("\n")} fallback={<>{props.value}</>}>
      <ul class="fw-list">
        <For each={lines()}>{(line) => <li>{line}</li>}</For>
      </ul>
    </Show>
  );
}

function FrameworkTab(props: { firm: FirmT }) {
  const firm = () => props.firm;
  return (
    <div class="tab-pane">
      <p class="framework-source">Source: {firm().framework_source}</p>
      <dl class="axes">
        <For each={AXIS_DEFS}>
          {(ax) => (
            <div class="axis">
              <dt>
                <span class="axis-label">{ax.label}</span>
                <AxisInfo def={ax} />
              </dt>
              <dd><FwValue value={firm().framework[ax.key]} /></dd>
            </div>
          )}
        </For>
      </dl>
      <div class="verdict">&#9656; {firm().framework.verdict}</div>
    </div>
  );
}

function fmtPctSigned(v: number | null | undefined, decimals = 1): string {
  if (v == null) return "n/a";
  const pct = v * 100;
  const sign = pct > 0 ? "+" : "";
  return `${sign}${pct.toFixed(decimals)}%`;
}

function PeerCell(props: {
  value: number | null;
  median: number | null;
  format: "money" | "pct";
  colorByMedian?: boolean;
  signedColor?: boolean;
}) {
  const text = () => {
    if (props.value == null) return "n/a";
    return props.format === "pct" ? fmtPctSigned(props.value) : fmtMoney(props.value);
  };
  const cls = () => {
    const v = props.value;
    if (v == null) return "";
    if (props.signedColor) {
      if (v > 0) return "pos";
      if (v < 0) return "neg";
      return "";
    }
    if (props.colorByMedian && props.median != null) {
      const diff = v - props.median;
      if (Math.abs(diff) / (Math.abs(props.median) || 1) < 0.05) return "";
      return diff > 0 ? "pos" : "neg";
    }
    return "";
  };
  return <span class={`peer-num ${cls()}`}>{text()}</span>;
}

function PeerTable(props: {
  peers: PeerRow[];
  median: PeerMedian;
  sector: string;
  activeTab: TabId;
}) {
  const tabQs = () => (props.activeTab !== "overview" ? `?tab=${props.activeTab}` : "");
  return (
    <section class="peers">
      <div class="peers-head">
        <div class="peers-label">Peers in {props.sector}</div>
        <div class="peers-hint">sorted by revenue, top {props.peers.length}</div>
      </div>
      <div class="peer-table-wrap">
        <table class="peer-table">
          <thead>
            <tr>
              <th class="col-firm">Firm</th>
              <th class="col-num">Revenue</th>
              <th class="col-num">Op margin</th>
              <th class="col-num">Free CF</th>
              <th class="col-num">Market cap</th>
            </tr>
          </thead>
          <tbody>
            <For each={props.peers}>
              {(p) => (
                <tr classList={{ "is-anchor": p.is_anchor }}>
                  <td class="col-firm">
                    <Show
                      when={!p.is_anchor}
                      fallback={
                        <span class="peer-firm anchor-firm">
                          <span class="peer-tk">{p.ticker ?? "?"}</span>
                          <span class="peer-nm">{p.name}</span>
                          <span class="anchor-tag">you are here</span>
                        </span>
                      }
                    >
                      <A class="peer-firm" href={`/firm/${p.slug}${tabQs()}`}>
                        <span class="peer-tk">{p.ticker ?? "?"}</span>
                        <span class="peer-nm">{p.name}</span>
                      </A>
                    </Show>
                  </td>
                  <td class="col-num">
                    <PeerCell value={p.revenue} median={props.median.revenue} format="money" colorByMedian />
                  </td>
                  <td class="col-num">
                    <PeerCell value={p.operating_margin} median={props.median.operating_margin} format="pct" signedColor />
                  </td>
                  <td class="col-num">
                    <PeerCell value={p.free_cash_flow} median={props.median.free_cash_flow} format="money" signedColor />
                  </td>
                  <td class="col-num">
                    <PeerCell value={p.market_cap} median={props.median.market_cap} format="money" colorByMedian />
                  </td>
                </tr>
              )}
            </For>
            <tr class="median-row">
              <td class="col-firm"><span class="median-label">cohort median</span></td>
              <td class="col-num"><span class="peer-num muted">{fmtMoney(props.median.revenue)}</span></td>
              <td class="col-num"><span class="peer-num muted">{fmtPctSigned(props.median.operating_margin)}</span></td>
              <td class="col-num"><span class="peer-num muted">{fmtMoney(props.median.free_cash_flow)}</span></td>
              <td class="col-num"><span class="peer-num muted">{fmtMoney(props.median.market_cap)}</span></td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default function Firm() {
  const params = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams<{ tab?: string }>();
  const [firm] = createResource(() => params.slug, fetchFirm);
  const [firmsRes] = createResource(fetchFirms);
  const [peersRes] = createResource(() => params.slug, (slug) => fetchPeers(slug, 6));

  const active = (): TabId => {
    const t = searchParams.tab;
    return t && (TABS as string[]).includes(t) ? (t as TabId) : "overview";
  };
  const setActive = (id: TabId) =>
    setSearchParams({ tab: id === "overview" ? undefined : id }, { replace: true });

  const sortedFirms = createMemo<FirmSummary[]>(() => {
    const items = firmsRes()?.items ?? [];
    return [...items].sort(
      (a, b) => (a.priority_order ?? 999) - (b.priority_order ?? 999),
    );
  });

  const currentIdx = createMemo(() =>
    sortedFirms().findIndex((f) => f.slug === params.slug),
  );
  const prevFirm = createMemo<FirmSummary | null>(() => {
    const i = currentIdx();
    return i > 0 ? sortedFirms()[i - 1] : null;
  });
  const nextFirm = createMemo<FirmSummary | null>(() => {
    const i = currentIdx();
    const arr = sortedFirms();
    return i >= 0 && i < arr.length - 1 ? arr[i + 1] : null;
  });

  const peers = createMemo<PeerRow[]>(() => peersRes()?.items ?? []);
  const peerMedian = createMemo<PeerMedian>(
    () =>
      peersRes()?.median ?? {
        revenue: null,
        operating_margin: null,
        free_cash_flow: null,
        market_cap: null,
      },
  );

  const kpis = createMemo<Kpi[]>(() => {
    const f = firm();
    return f ? buildKpis(f) : [];
  });

  const goTo = (slug: string) => {
    const tab = searchParams.tab;
    navigate(`/firm/${slug}${tab ? `?tab=${tab}` : ""}`);
  };

  onMount(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target;
      const typing =
        t instanceof HTMLInputElement ||
        t instanceof HTMLTextAreaElement ||
        (t instanceof HTMLElement && t.isContentEditable);
      if (typing) return;

      if (e.key === "[" || e.key === "ArrowLeft") {
        const p = prevFirm();
        if (p) {
          e.preventDefault();
          goTo(p.slug);
        }
      } else if (e.key === "]" || e.key === "ArrowRight") {
        const n = nextFirm();
        if (n) {
          e.preventDefault();
          goTo(n.slug);
        }
      } else if (/^[1-5]$/.test(e.key)) {
        const idx = parseInt(e.key, 10) - 1;
        const tabId = TABS[idx];
        if (tabId) {
          e.preventDefault();
          setActive(tabId);
        }
      }
    };
    document.addEventListener("keydown", handler);
    onCleanup(() => document.removeEventListener("keydown", handler));
  });

  const tabs = createMemo<{ id: TabId; label: string; enabled: boolean }[]>(() => {
    const f = firm();
    return TABS.map((id) => ({
      id,
      label: TAB_LABEL[id],
      enabled:
        id === "overview" ||
        id === "framework" ||
        (id === "operations" && !!f?.operations) ||
        (id === "financials" && !!f?.financials) ||
        (id === "ai_posture" && !!f?.ai_posture),
    }));
  });

  return (
    <Show when={firm()} fallback={<div class="loading">loading firm...</div>}>
      {(f) => (
        <article class="dash">
          <header class="dash-head">
            <div class="dash-head-left">
              <button
                type="button"
                class="back"
                title="Back (press g for grid)"
                onClick={(e) => {
                  e.preventDefault();
                  if (window.history.length > 1) navigate(-1);
                  else navigate("/firms");
                }}
              >
                &larr;
              </button>
              <FirmLogo
                slug={f().slug}
                ticker={f().ticker}
                hasLogo={!!f().branding?.logo_path}
                hasIcon={!!f().branding?.icon_path}
              />
              <div class="dash-title">
                <div class="t1">{f().name}</div>
                <div class="t2">
                  Operations command center, F500 #{f().f500_rank ?? "?"}, {f().sector}
                </div>
              </div>
            </div>
            <div class="dash-head-right">
              <div class="dash-nav" role="group" aria-label="firm navigation">
                <button
                  class="dash-nav-btn"
                  type="button"
                  onClick={() => prevFirm() && goTo(prevFirm()!.slug)}
                  disabled={!prevFirm()}
                  aria-label="Previous firm"
                  title={prevFirm() ? `Previous: ${prevFirm()!.name}  (← or [)` : "First firm"}
                >
                  &larr;
                </button>
                <span class="dash-nav-pos">
                  {currentIdx() >= 0 ? currentIdx() + 1 : "?"} / {sortedFirms().length || "?"}
                </span>
                <button
                  class="dash-nav-btn"
                  type="button"
                  onClick={() => nextFirm() && goTo(nextFirm()!.slug)}
                  disabled={!nextFirm()}
                  aria-label="Next firm"
                  title={nextFirm() ? `Next: ${nextFirm()!.name}  (→ or ])` : "Last firm"}
                >
                  &rarr;
                </button>
              </div>
              <Show when={f().financials}>
                {(fin) => <div class="fy-badge">FY{fin().fiscal_year}</div>}
              </Show>
            </div>
          </header>

          <Show when={f().pitch}>
            <section class="pitch-banner" aria-label="Pitch">{f().pitch}</section>
          </Show>

          <Show when={f().framework}>
            <section class="verdict-strip" aria-label="Structural verdict">
              <div class="vs-hero">
                <span class="vs-k">Verdict</span>
                <span class="vs-v">&#9656; {f().framework.verdict}</span>
              </div>
              <div class="vs-support">
                <div class="vs-row">
                  <span class="vs-k">Moat</span>
                  <span class="vs-v"><FwValue value={f().framework.surviving_moats} /></span>
                </div>
                <div class="vs-row">
                  <span class="vs-k">AI risk</span>
                  <span class="vs-v"><FwValue value={f().framework.compression_risk} /></span>
                </div>
              </div>
            </section>
          </Show>

          <Show when={f().financials}>
            <section class="kpi-grid kpi-grid-primary" aria-label="Headline metrics">
              <For each={kpis().slice(0, 4)}>{(k) => <KpiCard k={k} />}</For>
            </section>
            <div class="kpi-group-rule"><span>Secondary metrics</span></div>
            <section class="kpi-grid kpi-grid-secondary" aria-label="Secondary metrics">
              <For each={kpis().slice(4)}>{(k) => <KpiCard k={k} />}</For>
            </section>
          </Show>

          <nav class="tab-bar" role="tablist">
            <For each={tabs()}>
              {(t, i) => (
                <button
                  class="tab-btn"
                  classList={{ active: active() === t.id, disabled: !t.enabled }}
                  disabled={!t.enabled}
                  onClick={() => t.enabled && setActive(t.id)}
                  role="tab"
                  aria-selected={active() === t.id}
                  title={`${t.label} (press ${i() + 1})`}
                >
                  <span class="tab-top">
                    {t.label}
                    <span class="tab-key">{i() + 1}</span>
                  </span>
                  <span class="tab-hint">{TAB_HINT[t.id]}</span>
                </button>
              )}
            </For>
          </nav>

          <Show when={active() === "overview"}>
            <OverviewTab firm={f()} />
          </Show>
          <Show when={active() === "operations" && f().operations}>
            <OperationsTab ops={f().operations!} />
          </Show>
          <Show when={active() === "financials" && f().financials}>
            <FinancialsTab fin={f().financials!} />
          </Show>
          <Show when={active() === "ai_posture" && f().ai_posture}>
            <AiPostureTab posture={f().ai_posture!} />
          </Show>
          <Show when={active() === "framework"}>
            <FrameworkTab firm={f()} />
          </Show>

          <Show when={peers().length > 1}>
            <PeerTable
              peers={peers()}
              median={peerMedian()}
              sector={f().sector ?? ""}
              activeTab={active()}
            />
          </Show>

          <Show when={f().financials}>
            {(fin) => (
              <div class="dash-foot">
                <span>data: Massive.com</span>
                <span>
                  pulled {fin().source.pulled_at.slice(0, 19).replace("T", " ")} UTC
                </span>
                <span>filed {fin().filing_date}</span>
              </div>
            )}
          </Show>
        </article>
      )}
    </Show>
  );
}
